import {HStack, Text, VStack} from 'platform/foundation';
import {match} from 'ts-pattern';

import {useState} from 'react';

import i18n from '@dms/i18n';

import {suffixTestId, TestIdProps} from 'shared/web';

import {NlBpmEstimate} from '../utils/calculateNlBpm';
import {useBpmAmountFormatter} from '../utils/useBpmAmountFormatter';
import {BpmBreakdown} from './BpmBreakdown';
import {BpmScenarioTile} from './BpmScenarioTile';

type TwoScenarios = Extract<NlBpmEstimate, {status: 'twoScenarios'}>;

interface BpmScenariosProps extends TestIdProps {
  scenarios: TwoScenarios;
  subtitle: string;
  footnote: string;
  isFixedRate?: boolean;
  adPrice?: number | null;
}

/**
 * A car below 3 000 km can be assessed as new or as used. Both outcomes are
 * equally plausible, so the user switches between them and the selected one is
 * broken down exactly like a single estimate.
 */
export function BpmScenarios(props: BpmScenariosProps) {
  const formatAmount = useBpmAmountFormatter();
  // Below the mileage and age limits the tax office is more likely to assess
  // the car as new, so that scenario opens selected.
  const [selected, setSelected] = useState<'asNew' | 'asUsed'>('asNew');

  const selectedEstimate = match(selected)
    .with('asNew', () => props.scenarios.asNew)
    .with('asUsed', () => props.scenarios.asUsed)
    .exhaustive();

  const likelyNote = i18n.t('entity.sourcing.bpm.scenario.moreLikely');

  return (
    <VStack spacing={3}>
      <HStack spacing={2}>
        <BpmScenarioTile
          label={i18n.t('entity.sourcing.bpm.scenario.asNew')}
          value={formatAmount(props.scenarios.asNew)}
          note={likelyNote}
          isSelected={selected === 'asNew'}
          onSelect={() => setSelected('asNew')}
          data-testid={suffixTestId('asNew', props)}
        />
        <BpmScenarioTile
          label={i18n.t('entity.sourcing.bpm.scenario.asUsed')}
          value={formatAmount(props.scenarios.asUsed)}
          note={undefined}
          isSelected={selected === 'asUsed'}
          onSelect={() => setSelected('asUsed')}
          data-testid={suffixTestId('asUsed', props)}
        />
      </HStack>

      <BpmBreakdown
        estimate={selectedEstimate}
        grossBpm={props.scenarios.breakdown.grossBpm}
        depreciationPercent={
          selected === 'asUsed' ? props.scenarios.breakdown.depreciationPercent : 0
        }
        isFixedRate={props.isFixedRate}
        adPrice={props.adPrice}
        subtitle={props.subtitle}
        footnote={props.footnote}
        data-testid={suffixTestId('selected', props)}
      />

      <Text size="xSmall" color="secondary">
        {i18n.t('entity.sourcing.bpm.scenario.explanation')}
      </Text>
    </VStack>
  );
}
